import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Checkbox,
  Form,
  Input,
  InputNumber,
  List,
  Modal,
  Progress,
  Space,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import { ApiError } from '../../api/errors';
import {
  ProjectTaskProgressMethod,
  addProjectTaskChecklistItem,
  changeProjectTaskProgressMethod,
  getProjectTaskChecklist,
  removeProjectTaskChecklistItem,
  setProjectTaskChecklistItemState,
} from '../../api/projects';
import type {
  ProjectTaskChecklist,
  ProjectTaskChecklistItem,
  ProjectTaskItem,
} from '../../api/projects';
import { BrandColors } from '../../theme/brand';

/** Giá trị của biểu mẫu thêm mục kiểm. */
interface AddItemFormValues {
  readonly description: string;
  readonly isRequired: boolean;
  readonly weight: number | null;
}

/**
 * Hộp thoại danh sách kiểm của một công việc.
 *
 * <b>Đây là cách biến tiến độ từ cảm nhận thành thứ đếm được.</b> Phần trăm tự nhập không kiểm chứng
 * được, còn <i>đã lắp xong giá đỡ camera chưa</i> thì chỉ có hai câu trả lời và cả hai bên đều kiểm
 * được.
 *
 * <b>Danh sách kiểm chỉ điều khiển tiến độ khi công việc đã chuyển sang cách đo tương ứng.</b> Một
 * việc vẫn đo theo cách khác được phép có danh sách kiểm — khi đó nó là ghi chú việc cần làm. Hộp
 * thoại nói rõ công việc đang ở trạng thái nào, vì hứa sai rằng tick xong tiến độ sẽ nhúc nhích là
 * cách chắc chắn làm người dùng mất tin.
 *
 * <b>Ô trọng số chỉ hiện khi người dùng cần tới nó.</b> Mặc định mọi mục nặng như nhau, và phần lớn
 * trường hợp thế là đúng — bắt nghĩ về trọng số ở mỗi dòng là bắt làm một việc thừa.
 */
export function ProjectTaskChecklistModal({
  projectId,
  task,
  canManage,
  canReportProgress,
  open,
  onClose,
}: {
  readonly projectId: string;
  readonly task: ProjectTaskItem | null;
  readonly canManage: boolean;
  readonly canReportProgress: boolean;
  readonly open: boolean;

  /** Đóng hộp thoại. Nhận cờ cho biết có gì đã đổi để nơi gọi tải lại cây công việc. */
  readonly onClose: (hasChanged: boolean) => void;
}): ReactNode {
  const [form] = Form.useForm<AddItemFormValues>();

  const [checklist, setChecklist] = useState<ProjectTaskChecklist | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanged, setHasChanged] = useState(false);
  const [showWeight, setShowWeight] = useState(false);

  useEffect(() => {
    if (!open || task === null) {
      return;
    }

    let isCurrent = true;

    async function load(taskId: string): Promise<void> {
      setErrorMessage(null);

      try {
        const loaded = await getProjectTaskChecklist(projectId, taskId);

        if (isCurrent) {
          setChecklist(loaded);
        }
      } catch (error) {
        if (!isCurrent) {
          return;
        }

        setErrorMessage(
          error instanceof ApiError ? error.message : 'Không tải được danh sách kiểm.',
        );
      }
    }

    void load(task.id);

    return () => {
      isCurrent = false;
    };
  }, [projectId, task, open]);

  const close = useCallback(() => {
    onClose(hasChanged);
    setHasChanged(false);
    setChecklist(null);
  }, [hasChanged, onClose]);

  /** Chạy một thao tác đổi danh sách kiểm và nhận về danh sách mới. */
  async function apply(
    action: (taskId: string) => Promise<ProjectTaskChecklist>,
    failureMessage: string,
  ): Promise<void> {
    if (task === null) {
      return;
    }

    setIsSaving(true);

    try {
      setChecklist(await action(task.id));
      setHasChanged(true);
    } catch (error) {
      message.error(error instanceof ApiError ? error.message : failureMessage);
    } finally {
      setIsSaving(false);
    }
  }

  async function switchToChecklistMethod(): Promise<void> {
    if (task === null) {
      return;
    }

    setIsSaving(true);

    try {
      await changeProjectTaskProgressMethod(
        projectId,
        task.id,
        ProjectTaskProgressMethod.Checklist,
      );

      setChecklist(await getProjectTaskChecklist(projectId, task.id));
      setHasChanged(true);
      message.success('Từ giờ tiến độ của việc này được tính từ danh sách kiểm.');
    } catch (error) {
      message.error(error instanceof ApiError ? error.message : 'Không đổi được cách đo.');
    } finally {
      setIsSaving(false);
    }
  }

  async function submit(values: AddItemFormValues): Promise<void> {
    await apply(
      (taskId) =>
        addProjectTaskChecklistItem(projectId, taskId, {
          description: values.description,
          isRequired: values.isRequired,
          weight: values.weight ?? null,
          sequenceNumber: 0,
          note: null,
        }),
      'Không thêm được mục kiểm.',
    );

    form.resetFields();
  }

  const items = checklist?.items ?? [];
  const isDriven = checklist?.isProgressDrivenByChecklist ?? false;

  return (
    <Modal
      open={open}
      title={task === null ? 'Danh sách kiểm' : `Danh sách kiểm — ${task.wbsCode} ${task.name}`}
      width={760}
      footer={null}
      onCancel={close}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {errorMessage !== null ? <Alert type="error" showIcon message={errorMessage} /> : null}

        {task?.isSummaryTask === true ? (
          <Alert
            type="info"
            showIcon
            message="Đây là một nhóm việc nên không có danh sách kiểm"
            description="Tiến độ của nhóm việc được tính từ các việc con lên. Hãy thêm mục kiểm vào từng việc con."
          />
        ) : null}

        {checklist !== null && !isDriven && task?.isSummaryTask === false ? (
          <Alert
            type="warning"
            showIcon
            message="Việc này chưa đo tiến độ bằng danh sách kiểm"
            description="Tick các mục ở đây sẽ không làm tiến độ thay đổi — danh sách đang chỉ là ghi chú việc cần làm. Chuyển cách đo thì phần trăm sẽ được tính từ trọng số các mục đã tick, và không gõ tay được nữa."
            action={
              canReportProgress ? (
                <Button
                  size="small"
                  type="primary"
                  loading={isSaving}
                  onClick={() => {
                    void switchToChecklistMethod();
                  }}
                >
                  Đo bằng danh sách kiểm
                </Button>
              ) : null
            }
          />
        ) : null}

        {checklist !== null ? (
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            <Progress
              percent={Number(checklist.progressPercent.toFixed(1))}
              status={isDriven ? 'active' : 'normal'}
            />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {items.length === 0
                ? 'Chưa có mục kiểm nào.'
                : `${items.filter((item) => item.isCompleted).length.toString()}/${items.length.toString()} mục đã xong`}
              {checklist.unfinishedRequiredCount > 0
                ? ` — còn ${checklist.unfinishedRequiredCount.toString()} mục bắt buộc chưa xong, chưa đóng được việc.`
                : ''}
            </Typography.Text>
          </Space>
        ) : null}

        <List<ProjectTaskChecklistItem>
          size="small"
          bordered
          dataSource={items as ProjectTaskChecklistItem[]}
          loading={checklist === null && errorMessage === null}
          locale={{ emptyText: 'Chưa có mục kiểm nào. Hãy chia việc này thành những bước kiểm được.' }}
          renderItem={(item) => (
            <List.Item
              actions={
                canManage
                  ? [
                      <Tooltip key="remove" title="Xóa mục kiểm">
                        <Button
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => {
                            void apply(
                              (taskId) =>
                                removeProjectTaskChecklistItem(projectId, taskId, item.id),
                              'Không xóa được mục kiểm.',
                            );
                          }}
                        />
                      </Tooltip>,
                    ]
                  : []
              }
            >
              <Space size={8} wrap>
                <Checkbox
                  checked={item.isCompleted}
                  disabled={!canReportProgress || isSaving}
                  onChange={(event) => {
                    void apply(
                      (taskId) =>
                        setProjectTaskChecklistItemState(
                          projectId,
                          taskId,
                          item.id,
                          event.target.checked,
                        ),
                      'Không đổi được trạng thái mục kiểm.',
                    );
                  }}
                >
                  <span
                    style={{
                      textDecoration: item.isCompleted ? 'line-through' : undefined,
                      color: item.isCompleted ? BrandColors.inkMuted : undefined,
                    }}
                  >
                    {item.description}
                  </span>
                </Checkbox>
                {item.isRequired ? (
                  <Tooltip title="Chưa tick mục này thì chưa đánh dấu hoàn thành công việc được.">
                    <Tag color="red">Bắt buộc</Tag>
                  </Tooltip>
                ) : null}
                {item.weight === 1 ? null : (
                  <Tooltip title="Trọng số khi tính phần trăm. Mục nặng hơn thì tick xong tiến độ nhúc nhích nhiều hơn.">
                    <Tag>Trọng số {item.weight.toString()}</Tag>
                  </Tooltip>
                )}
              </Space>
            </List.Item>
          )}
        />

        {canManage && task?.isSummaryTask === false ? (
          <Form<AddItemFormValues>
            form={form}
            layout="inline"
            initialValues={{ isRequired: false }}
            onFinish={(values) => void submit(values)}
          >
            <Form.Item
              name="description"
              rules={[{ required: true, message: 'Chưa nhập nội dung mục kiểm.' }]}
              style={{ flex: 1, minWidth: 260 }}
            >
              <Input placeholder="Việc cần làm, ví dụ: đã chạy thử liên tục tám tiếng" />
            </Form.Item>
            <Form.Item name="isRequired" valuePropName="checked">
              <Checkbox>Bắt buộc</Checkbox>
            </Form.Item>
            {showWeight ? (
              <Form.Item name="weight">
                <InputNumber<number>
                  min={0.1}
                  max={1000}
                  step={1}
                  placeholder="Trọng số"
                  style={{ width: 120 }}
                />
              </Form.Item>
            ) : null}
            <Form.Item>
              <Button type="primary" htmlType="submit" icon={<PlusOutlined />} loading={isSaving}>
                Thêm
              </Button>
            </Form.Item>
            <Form.Item>
              <Button
                type="link"
                size="small"
                onClick={() => {
                  setShowWeight((current) => !current);
                }}
              >
                {showWeight ? 'Ẩn trọng số' : 'Đặt trọng số'}
              </Button>
            </Form.Item>
          </Form>
        ) : null}

        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Mục kiểm nên là câu chỉ có hai câu trả lời. Một dòng dài tới mức tick được một nửa là dấu
          hiệu nó nên tách thành nhiều mục.
        </Typography.Text>
      </Space>
    </Modal>
  );
}
